<script lang="ts" setup>
import call from "@/assets/image/icons/call.svg";
import mail from "@/assets/image/icons/mail.svg";
import homePin from "@/assets/image/icons/home-pin.svg";
import { EMAIL_REGEX } from "@/assets/ts/helpers";
import api from "@/api";
import { $t } from "@/i18n";
import { useDefaultStore } from "@/store";
import { $te } from "@/assets/ts/helpers";

const isLoading = ref(false);
const store = useDefaultStore();
const messageBelow = ref<null | string>(null);
const messageType = ref<"success" | "error" | null>(null);
const isValidEmail = computed(() => EMAIL_REGEX.test(form?.email.trim()));

async function submitForm() {
  isLoading.value = true;

  if (!isValidEmail.value) {
    // toastStore.openToastError($t("app.contactPage.invalidEmailMsg"));
    messageBelow.value = $t("app.contactPage.invalidEmailMsg");
    messageType.value = "error";
    isLoading.value = false;
    return;
  }

  const params = {
    name: form.name,
    phone: form.phone,
    city: form.city,
    message: form.message,
    email: form.email,
  };

  try {
    const res = await api.contactUs(params);
    console.warn(res.data.message);
    // toastStore.openToastSuccess($t("app.contactPage.successMsg"));
    messageBelow.value = $t("app.contactPage.successMsg");
    messageType.value = "success";
    form.email = "";
    form.name = "";
    form.phone = "";
    form.city = "";
    form.message = "";
  } catch (error: any) {
    console.error(error);
    // useToastStore().openToastError($t("app.contactPage.errorMsg"));
    messageBelow.value = $t("app.contactPage.errorMsg");
    messageType.value = "error";
  } finally {
    isLoading.value = false;
  }
}

useHead({
  title: `${$te("app.base.contact")}`,
  meta: [
    {
      name: "title",
      content: `${$te("app.base.contact")}`,
    },
    {
      name: "description",
      content: $te("app.contactPage.contactInfoText"),
    },
    { name: "robots", content: "index, follow" },
    {
      property: "og:title",
      content: `${$te("app.base.contact")}`,
    },
    {
      property: "og:description",
      content: $te("app.contactPage.contactInfoText"),
    },
    { property: "og:site_name", content: store.SITE_NAME },
  ],
});

const form = reactive({
  email: "",
  name: "",
  phone: "",
  city: "",
  message: "",
});

const isValidForm = computed(() => {
  const hasFields = !!(
    form.name &&
    form.name.trim() &&
    form.email &&
    form.email.trim() &&
    form.phone &&
    form.phone.trim() &&
    form.city &&
    form.city.trim() &&
    form.message &&
    form.message.trim()
  );
  if (hasFields) return true;
  return false;
});

watch(
  form,
  () => {
    if (messageType.value !== "success") {
      messageBelow.value = null;
      messageType.value = null;
    }
  },
  { deep: true }
);
</script>

<template>
  <m-page
    class="bg-text-dark text-text-dark bg-[linear-gradient(126.83deg,_#101010_33.34%,_rgba(16,16,16,0.6)_98.79%),url('@/assets/image/hero/hero-solarni-paneli.png')] bg-cover bg-bottom-right bg-no-repeat"
  >
    <h1 class="sr-only hidden">{{ $t("app.base.contact") }}</h1>
    <div
      class="border-primary bg-text-light mx-auto flex max-w-screen-xl flex-col-reverse overflow-hidden rounded-3xl border-[3px] md:flex-row"
    >
      <div
        class="rounded-t-3xl bg-[linear-gradient(#F0F0F0CC,#F0F0F0CC),url('@/assets/image/contactPage/kontakt-slika.jpg')] bg-cover bg-center bg-no-repeat p-8 pb-48 md:mb-0 md:min-h-full md:w-[41%] md:rounded-none"
      >
        <h2 class="pb-3 text-2xl leading-[120%] font-semibold">
          {{ $t("app.contactPage.contactInfoTitle") }}
        </h2>
        <p class="pb-18 text-lg leading-[150%]">
          {{ $t("app.contactPage.contactInfoText") }}
        </p>
        <div class="space-y-12">
          <div class="flex space-x-5">
            <img :src="call" alt="call" /><a href="tel:+38162491909" class="text-[22px] font-semibold">062/491909</a>
          </div>
          <div class="flex space-x-5">
            <img :src="mail" alt="mail" /><a
              href="mailto:info@icmelektro.com"
              style="overflow-wrap: anywhere"
              class="text-[22px] font-semibold"
              >info@icmelektro.com</a
            >
          </div>
          <div class="flex space-x-5">
            <img :src="homePin" alt="home pin" /><span class="text-[22px] font-semibold">{{ $t("app.contactPage.city") }}</span>
          </div>
        </div>
      </div>
      <div class="flex-1 px-4 pt-10 pb-6 md:w-[59%] md:pr-6 md:pb-9 md:pl-5">
        <h2 class="mb-12 text-2xl leading-[120%] font-semibold">{{ $t("app.contactPage.contactUsTitle") }}</h2>
        <div class="mb-12">
          <div class="mb-10 flex flex-col flex-wrap space-y-5 md:flex-row md:space-x-5">
            <!-- Name -->
            <input
              v-model="form.name"
              type="text"
              :placeholder="$t('app.contactPage.form.name')"
              class="font-montserrat text-text-dark border-text-grey h-[37px] border-b px-[10px] py-[5px] text-[18px] leading-[150%] font-semibold focus:border-red-500 focus:placeholder-red-500 focus:outline-none"
            />

            <!-- Email -->
            <input
              v-model="form.email"
              type="email"
              :placeholder="$t('app.contactPage.form.email')"
              class="font-montserrat text-text-dark border-text-grey h-[37px] border-b px-[10px] py-[5px] text-[18px] leading-[150%] font-semibold focus:border-red-500 focus:placeholder-red-500 focus:outline-none"
            />
          </div>

          <div class="mb-10 flex flex-col flex-wrap space-y-5 md:flex-row md:space-x-5">
            <!-- Phone -->
            <input
              v-model="form.phone"
              type="text"
              :placeholder="$t('app.contactPage.form.phone')"
              class="font-montserrat text-text-dark border-text-grey h-[37px] border-b px-[10px] py-[5px] text-[18px] leading-[150%] font-semibold focus:border-red-500 focus:placeholder-red-500 focus:outline-none"
            />

            <!-- City -->
            <input
              v-model="form.city"
              type="text"
              :placeholder="$t('app.contactPage.form.city')"
              class="font-montserrat text-text-dark border-text-grey h-[37px] border-b px-[10px] py-[5px] text-[18px] leading-[150%] font-semibold focus:border-red-500 focus:placeholder-red-500 focus:outline-none"
            />
          </div>

          <div class="relative mb-12">
            <textarea
              v-model="form.message"
              :placeholder="$t('app.contactPage.form.message')"
              class="font-montserrat text-text-dark border-text-grey h-[207px] w-full resize-none border-b px-[10px] py-[5px] text-[18px] leading-[150%] font-semibold focus:border-red-500 focus:placeholder-red-500 focus:outline-none"
            ></textarea>

            <p
              v-if="messageBelow"
              class="absolute -bottom-8 left-0 text-sm font-medium md:-bottom-6"
              :class="messageType === 'success' ? 'text-green-500' : 'text-red-500'"
            >
              {{ messageBelow }}
            </p>
          </div>
        </div>
        <client-only>
          <base-button
            :disabled="!isValidForm || isLoading"
            class="ml-auto w-fit"
            :text="$t('app.contactPage.buttonLabel')"
            @click="submitForm"
          ></base-button>
        </client-only>
      </div>
    </div>
  </m-page>
</template>
